import React, { useCallback, useEffect, useState } from "react";
import { push } from "connected-react-router";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import Container from "@material-ui/core/Container";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import HeaderBarSub from "components/HeaderBarSub";
import GridFullHeight from "components/GridFullHeight";
import GridFormContainer from "components/GridFormContainer";
import Loading from "components/Loading";
import Input from "components/Input";
import ErrorMessage from "components/ErrorMessage";
import SubmitButton from "components/Button";
import { useTranslation } from "react-i18next";
import {
  AppVersioning,
  getVersioningPage,
  updateVersioningPage,
} from "api/staticPages";
import { SET_MESSAGES_REDUCER } from "redux/reducers/messages/actionTypes";
import imgLoading from "asset/img/loading.gif";
import useStyles from "./styles";

interface ManageAppUpdateProps {
  dispatch: Dispatch;
}

const emptyForm: AppVersioning = {
  min_version: "",
  force_update: false,
  ios_link: "",
  android_link: "",
};

const MIN_VERSION_PATTERN = /^\d+\.\d+\.\d+$/;

type MinVersionError = "required" | "pattern" | null;

const validateMinVersion = (value: string | number): MinVersionError => {
  const trimmed = String(value).trim();
  if (!trimmed) return "required";
  if (!MIN_VERSION_PATTERN.test(trimmed)) return "pattern";
  return null;
};

const validateRequiredLink = (value: string): boolean =>
  Boolean(value.trim());

const ManageAppUpdate = React.memo((props: ManageAppUpdateProps) => {
  const { dispatch } = props;
  const classes = useStyles();
  const translate = useTranslation().t;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState<AppVersioning>(emptyForm);
  const [minVersionError, setMinVersionError] = useState<MinVersionError>(null);
  const [iosLinkError, setIosLinkError] = useState(false);
  const [androidLinkError, setAndroidLinkError] = useState(false);

  const openUrl = (url: string) => {
    dispatch(push(url));
  };

  const loadVersioning = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getVersioningPage();
      setForm({
        min_version: data.min_version ?? "",
        force_update: Boolean(data.force_update),
        ios_link: data.ios_link ?? "",
        android_link: data.android_link ?? "",
      });
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : translate("FAILED_TO_LOAD_APP_VERSIONING")
      );
      setForm(emptyForm);
    } finally {
      setLoading(false);
    }
  }, [translate]);

  useEffect(() => {
    void loadVersioning();
  }, [loadVersioning]);

  const handleUpdate = async () => {
    const versionError = validateMinVersion(form.min_version);
    const iosError = !validateRequiredLink(form.ios_link);
    const androidError = !validateRequiredLink(form.android_link);

    setMinVersionError(versionError);
    setIosLinkError(iosError);
    setAndroidLinkError(androidError);

    if (versionError || iosError || androidError) return;

    setSaving(true);
    try {
      await updateVersioningPage({
        min_version: String(form.min_version).trim(),
        force_update: form.force_update,
        ios_link: form.ios_link.trim(),
        android_link: form.android_link.trim(),
      });
      dispatch({
        type: SET_MESSAGES_REDUCER,
        message: {
          type: "success",
          key: "APP_VERSIONING_UPDATED_SUCCESSFULLY",
          message: translate("APP_VERSIONING_UPDATED_SUCCESSFULLY"),
        },
      });
    } catch {
      dispatch({
        type: SET_MESSAGES_REDUCER,
        message: {
          type: "error",
          key: "FAILED_TO_UPDATE_APP_VERSIONING",
          message: "Something went wrong. Please try again.",
        },
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <GridFullHeight container>
      <GridFormContainer item xs={12} sm={12} md={12}>
        <GridFullHeight container>
          <HeaderBarSub
            backUrl="/"
            openUrl={openUrl}
            name={translate("MANAGE_APP_UPDATE")}
          />
          <Container className={classes.root}>
            {loading ? (
              <Loading />
            ) : error ? (
              <p className={classes.error}>{error}</p>
            ) : (
              <form
                className={classes.form}
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleUpdate();
                }}
              >
                <Input
                  name="min_version"
                  type="string"
                  fullWidth
                  required
                  label={translate("MIN_VERSION")}
                  placeholder="2.8.8"
                  value={form.min_version}
                  changeHandler={(value: string) => {
                    setForm((prev) => ({ ...prev, min_version: value }));
                    if (minVersionError) {
                      setMinVersionError(validateMinVersion(value));
                    }
                  }}
                />
                {minVersionError === "required" && (
                  <ErrorMessage>
                    {translate("REQUIRED_INPUT_CTA", {
                      value: translate("MIN_VERSION"),
                    })}
                  </ErrorMessage>
                )}
                {minVersionError === "pattern" && (
                  <ErrorMessage>
                    {translate("MIN_VERSION_INVALID_FORMAT")}
                  </ErrorMessage>
                )}
                <Input
                  name="force_update"
                  type="checkbox"
                  label={translate("FORCE_UPDATE")}
                  checked={form.force_update}
                  changeHandler={(checked: boolean) =>
                    setForm((prev) => ({ ...prev, force_update: checked }))
                  }
                />
                <Input
                  name="ios_link"
                  type="string"
                  fullWidth
                  required
                  label={translate("IOS_LINK")}
                  placeholder="https://apps.apple.com/..."
                  value={form.ios_link}
                  changeHandler={(value: string) => {
                    setForm((prev) => ({ ...prev, ios_link: value }));
                    if (iosLinkError) {
                      setIosLinkError(!validateRequiredLink(value));
                    }
                  }}
                  showLinkIcon
                />
                {iosLinkError && (
                  <ErrorMessage>
                    {translate("REQUIRED_INPUT_CTA", {
                      value: translate("IOS_LINK"),
                    })}
                  </ErrorMessage>
                )}
                <Input
                  name="android_link"
                  type="string"
                  fullWidth
                  required
                  label={translate("ANDROID_LINK")}
                  placeholder="https://play.google.com/..."
                  value={form.android_link}
                  changeHandler={(value: string) => {
                    setForm((prev) => ({ ...prev, android_link: value }));
                    if (androidLinkError) {
                      setAndroidLinkError(!validateRequiredLink(value));
                    }
                  }}
                  showLinkIcon
                />
                {androidLinkError && (
                  <ErrorMessage>
                    {translate("REQUIRED_INPUT_CTA", {
                      value: translate("ANDROID_LINK"),
                    })}
                  </ErrorMessage>
                )}
                <ButtonGroup fullWidth className={classes.submitGroup}>
                  <SubmitButton
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    fullWidth
                  >
                    {saving ? (
                      <img alt="loading" src={imgLoading} width="30px" />
                    ) : (
                      translate("UPDATE")
                    )}
                  </SubmitButton>
                </ButtonGroup>
              </form>
            )}
          </Container>
        </GridFullHeight>
      </GridFormContainer>
    </GridFullHeight>
  );
});

export default connect()(ManageAppUpdate);
